/**
 * @fileoverview System-web admin shell layout with global navigation, breadcrumbs, and utility surfaces.
 * @module system-web/layout
 *
 * Responsibilities:
 * - Render persistent admin navigation and routed page content.
 * - Surface department library navigation and shared shell UI.
 *
 * Dependencies:
 * - react-router-dom
 * - ../auth/AuthProvider
 * - @ui-admin/contexts/ThemeContext
 *
 * Architecture:
 * - Layout owns shell chrome only; routed pages are responsible for local-first data access.
 * - Navigation should expose synced library surfaces without embedding page-specific data reads.
 *
 * Notes:
 * - Route labels and library navigation items must stay aligned with App route registration.
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { Users, Settings, LayoutDashboard, List, Shield, Crown, Package, Flag, ScrollText, Beaker, Upload, Building2, Activity, ChevronRight, BarChart3, FileText, Sparkles, ClipboardList, History, BookOpen, DatabaseZap, Database, GitMerge, Play, CalendarClock, Wrench } from 'lucide-react';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { useAuth } from '../auth/AuthProvider';
import SystemTestingModal from '../components/SystemTestingModal';
import DefensibleDataWidget from '../components/DefensibleDataWidget';
import { useEffectiveTheme } from '../hooks/useEffectiveTheme';

import { useTheme, themeCSS, FIRE_SMOKE_CSS, THEMES } from '@ui-admin/contexts/ThemeContext';
import { UserProfileDropdown } from '@firesquirrel/ui-admin';
import { formatDisplayVersion } from '@firesquirrel/web-config-client';
import { AIChatbot, useAIChat } from '@firesquirrel/ai-chatbot';
import squirrelIconSmall from '@firesquirrel/ai-chatbot/assets/squirrel_icon_small.png';
// DO NOT re-add BackgroundJobsIndicator to this layout. Per product direction,
// system-web does not show a global background-jobs tether in the sidebar.

const routeLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/system-status': 'System Status',
  '/mongodb': 'MongoDB Dashboard',
  '/ai-operations': 'AI Operations',
  '/sync-health': 'Sync Health',
  '/organizations': 'Organizations',
  '/tenants': 'Departments',
  '/tenants/new': 'New Department',
  '/users': 'System Users',
  '/users/invite': 'Invite User',
  '/lookups': 'Lookups',
  '/system-roles': 'System Roles',
  '/tiers': 'Tiers',
  '/modules': 'Modules',
  '/feature-flags': 'Feature Flags',
  '/portal-config': 'Customer Portal',
  '/audit-logs': 'Audit Logs',
  '/data-repair': 'Data Repair',
  '/data-repair/delivery-duplicates': 'Delivery Duplicates',
  '/import-templates': 'Import Templates',
  '/import-templates/new': 'New Template',
  '/violation-code-sets': 'Violation Code Sets',
  '/import-runs': 'Import Runs',
  '/import-runs/new': 'New Import',
  '/data-report-templates': 'Data Reports',
  '/data-report-templates/new': 'New Report',
  '/dashboard-templates': 'Dashboard Templates',
  '/dashboard-templates/new': 'New Dashboard',
  '/document-templates': 'Document Templates',
  '/document-templates/new': 'New Document',
  '/email-templates': 'Email Templates',
  '/email-templates/new': 'New Email Template',
  '/settings': 'Settings',
  '/inspection-templates': 'Inspection Templates',
  '/inspection-templates/new': 'New Inspection Template',
  '/inspection-programs': 'Inspection Programs',
  '/legacy-migration': 'Legacy Migration',
  '/legacy-migration/schema': 'Schema Manifest',
  '/legacy-migration/datasets': 'Datasets',
  '/legacy-migration/runs': 'Migration Runs',
  '/legacy-migration/department-runs': 'Department Migration Dashboard',
};

const adminNavItems = [
  { to: '/organizations', icon: Building2, label: 'Organizations' },
  { to: '/tenants', icon: Building2, label: 'Departments' },
  { to: '/users', icon: Users, label: 'System Users' },
  { to: '/system-roles', icon: Shield, label: 'System Roles' },
  { to: '/tiers', icon: Crown, label: 'Tiers' },
  { to: '/modules', icon: Package, label: 'Modules' },
  { to: '/feature-flags', icon: Flag, label: 'Feature Flags' },
  { to: '/portal-config', icon: Package, label: 'Customer Portal' },
  { to: '/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { to: '/data-repair', icon: Wrench, label: 'Data Repair' },
];

const libraryNavItems = [
  { to: '/lookups', icon: List, label: 'Lookups', badge: 'Seeded' as const },
  { to: '/inspection-templates', icon: ClipboardList, label: 'Inspection Templates' },
  { to: '/inspection-programs', icon: CalendarClock, label: 'Inspection Programs' },
  { to: '/violation-code-sets', icon: BookOpen, label: 'Violation Code Sets' },
  { to: '/import-templates', icon: Upload, label: 'Import Templates' },
  { to: '/import-runs', icon: History, label: 'Import Runs' },
  { to: '/data-report-templates', icon: BarChart3, label: 'Data Reports' },
  { to: '/dashboard-templates', icon: LayoutDashboard, label: 'Dashboards' },
  { to: '/document-templates', icon: FileText, label: 'Documents & Email' },
];

const migrationNavItems = [
  { to: '/legacy-migration/schema', icon: Database, label: 'Schema Manifest' },
  { to: '/legacy-migration/datasets', icon: GitMerge, label: 'Datasets' },
  { to: '/legacy-migration/runs', icon: Play, label: 'Migration Runs' },
];

/**
 * @component AdminLayout
 *
 * Purpose:
 * - Provides the system-web application shell, navigation flyouts, and routed content container.
 *
 * Props:
 * - None.
 *
 * Behavior:
 * - Displays admin navigation, department library links, breadcrumbs, theme-aware shell styling, and the active route outlet.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not perform feature-specific API reads or writes for routed pages.
 *
 * Performance:
 * - Memoizes navigation-derived state and renders shell-level UI once per route transition.
 */
export default function AdminLayout() {
  const { user, logout } = useAuth();

  const [testingOpen, setTestingOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { getBreadcrumbLabel } = useBreadcrumbs();
  const { isOpen: isChatOpen, toggleChat } = useAIChat();
  const { userThemeId, source: themeSource, setUserThemeId } = useEffectiveTheme();

  const breadcrumbs = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const crumbs: { label: string; path: string }[] = [
      { label: 'Dashboard', path: '/dashboard' },
    ];
    let currentPath = '';
    for (const segment of pathSegments) {
      currentPath += `/${segment}`;
      if (currentPath === '/dashboard') {
        continue;
      }
      const dynamicLabel = getBreadcrumbLabel(currentPath);
      const staticLabel = routeLabels[currentPath];
      const label = dynamicLabel || staticLabel || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      // Email templates have no standalone list route — breadcrumb link points to the unified Documents & Email grid
      const linkPath = currentPath === '/email-templates' ? '/document-templates' : currentPath;
      crumbs.push({ label, path: linkPath });
    }
    return crumbs;
  }, [location.pathname, getBreadcrumbLabel]);
  const { theme, themeId, setThemeId } = useTheme();
  const themeCssString = useMemo(() => themeCSS(theme), [theme]);
  const isFireTheme = themeId === 'fire-smoke' || themeId === 'fire-service';

  const adminBtnRef = useRef<HTMLButtonElement>(null);
  const libraryBtnRef = useRef<HTMLButtonElement>(null);
  const migrationBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!adminBtnRef.current?.contains(target) && !target.closest('[data-admin-flyout]')) {
        setAdminOpen(false);
      }
      if (!libraryBtnRef.current?.contains(target) && !target.closest('[data-library-flyout]')) {
        setLibraryOpen(false);
      }
      if (!migrationBtnRef.current?.contains(target) && !target.closest('[data-migration-flyout]')) {
        setMigrationOpen(false);
      }
      if (!profileDropdownRef.current?.contains(target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const adminRoutes = adminNavItems.map(i => i.to);
  const libraryRoutes = libraryNavItems.map(i => i.to);
  const migrationRoutes = migrationNavItems.map(i => i.to);
  const isOnAdmin = adminRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + '/'));
  const isOnLibrary = libraryRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + '/'));
  const isOnMigration = migrationRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + '/'));

  const flyoutLinkClass = 'flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150';

  const handleFlyoutHover = (e: React.MouseEvent<HTMLAnchorElement>, enter: boolean) => {
    e.currentTarget.style.background = enter ? theme.sidebarHoverBg : 'transparent';
  };

  const navLinkStyle = (isActive: boolean) => ({
    background: isActive ? theme.sidebarActiveBg : 'transparent',
    color: isActive ? theme.sidebarActiveText : theme.sidebarText,
    fontWeight: isActive ? 500 : 400,
  });

  const handleNavHover = (e: React.MouseEvent<HTMLAnchorElement>, enter: boolean) => {
    const el = e.currentTarget;
    if (!el.getAttribute('aria-current')) {
      el.style.background = enter ? theme.sidebarHoverBg : 'transparent';
    }
  };

  return (
    <div className="flex h-screen" style={{ background: theme.pageBg }} data-theme={themeId}>
      <style>{themeCssString}</style>
      {isFireTheme && <style>{FIRE_SMOKE_CSS}</style>}

      <aside
        className="w-64 flex flex-col shrink-0 relative"
        style={{ background: theme.sidebarBg, borderRight: `1px solid ${theme.sidebarBorder}` }}
      >
        <div className="p-4" style={{ borderBottom: `1px solid ${theme.sidebarBorder}`, color: theme.sidebarText }}>
          <div className="flex items-center gap-3.5">
            <div className="h-8 w-8 shrink-0 overflow-hidden rounded-sm">
              <img
                src="/Defensible-Connect-Logo-WEB-768px.png"
                alt="Defensible"
                className="h-12 w-12 max-w-none object-cover object-top"
                style={{ transform: 'translate(-8px, -2px)' }}
              />
            </div>
            <div className="flex min-w-0 flex-col justify-center">
              <span
                style={{
                  fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif",
                  fontStyle: 'italic',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: theme.sidebarText,
                  letterSpacing: '-0.01em',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.1,
                }}
              >
                Defensible
              </span>
              <span
                style={{
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 500,
                  fontSize: '0.6rem',
                  color: theme.sidebarText,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  opacity: 0.65,
                  marginLeft: '0.08rem',
                }}
              >
                Admin Portal
              </span>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 flex flex-col overflow-y-auto">
          <div className="flex-1 space-y-0.5">

            {/* Top-level direct links */}
            <NavLink
              to="/system-status"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <Activity className="h-4 w-4 shrink-0" />
              System Status
            </NavLink>
            <NavLink
              to="/mongodb"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <Database className="h-4 w-4 shrink-0" />
              MongoDB Dashboard
            </NavLink>
            <NavLink
              to="/ai-operations"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <Sparkles className="h-4 w-4 shrink-0" />
              AI Operations
            </NavLink>
            <NavLink
              to="/sync-health"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <Activity className="h-4 w-4 shrink-0" />
              Sync Health
            </NavLink>
            <NavLink
              to="/dashboard"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <LayoutDashboard className="h-4 w-4 shrink-0" />
              Platform Overview
            </NavLink>

            <div className="pt-1" style={{ borderTop: `1px solid ${theme.sidebarBorder}` }} />

            {/* Administration flyout */}
            <div
              className="relative"
            >
              <button
                ref={adminBtnRef}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  background: (isOnAdmin || adminOpen) ? theme.sidebarActiveBg : 'transparent',
                  color: (isOnAdmin || adminOpen) ? theme.sidebarActiveText : theme.sidebarText,
                }}
                onClick={() => {
                  if (adminOpen) {
                    setAdminOpen(false);
                    return;
                  }
                  setLibraryOpen(false);
                  setAdminOpen(true);
                }}
              >
                <Shield className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">Administration</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: theme.sidebarTextMuted }} />
              </button>

              {adminOpen && createPortal(
                <div
                  data-admin-flyout
                  className="fixed z-50"
                  style={(() => {
                    const btn = adminBtnRef.current;
                    if (!btn) return { left: 264, top: 60 };
                    const rect = btn.getBoundingClientRect();
                    const panelH = 300;
                    const gap = 8;
                    const left = 256 + gap;
                    const maxTop = Math.max(8, window.innerHeight - panelH - 8);
                    const top = Math.min(Math.max(8, rect.top), maxTop);
                    return { left, top };
                  })()}
                >
                  <div
                    className="flex flex-col gap-1 p-2 rounded-xl shadow-2xl"
                    style={{ minWidth: '200px', background: theme.sidebarBg, border: `1px solid ${theme.sidebarBorder}` }}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider px-2 pb-1" style={{ color: theme.sidebarTextMuted }}>
                      Administration
                    </p>
                    {adminNavItems.map(({ to, icon: Icon, label }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setAdminOpen(false)}
                        className={flyoutLinkClass}
                        style={({ isActive }) => ({ color: isActive ? theme.sidebarActiveText : theme.sidebarText, background: isActive ? theme.sidebarActiveBg : 'transparent' })}
                        onMouseEnter={(e) => handleFlyoutHover(e, true)}
                        onMouseLeave={(e) => handleFlyoutHover(e, false)}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>

            {/* Department Library flyout */}
            <div
              className="relative"
            >
              <button
                ref={libraryBtnRef}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  background: (isOnLibrary || libraryOpen) ? theme.sidebarActiveBg : 'transparent',
                  color: (isOnLibrary || libraryOpen) ? theme.sidebarActiveText : theme.sidebarText,
                }}
                onClick={() => {
                  if (libraryOpen) {
                    setLibraryOpen(false);
                    return;
                  }
                  setAdminOpen(false);
                  setLibraryOpen(true);
                }}
              >
                <ClipboardList className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">Department Library</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: theme.sidebarTextMuted }} />
              </button>

              {libraryOpen && createPortal(
                <div
                  data-library-flyout
                  className="fixed z-50"
                  style={(() => {
                    const btn = libraryBtnRef.current;
                    if (!btn) return { left: 264, top: 120 };
                    const rect = btn.getBoundingClientRect();
                    const panelH = 260;
                    const gap = 8;
                    const left = 256 + gap;
                    const maxTop = Math.max(8, window.innerHeight - panelH - 8);
                    const top = Math.min(Math.max(8, rect.top), maxTop);
                    return { left, top };
                  })()}
                >
                  <div
                    className="flex flex-col gap-1 p-2 rounded-xl shadow-2xl"
                    style={{ minWidth: '220px', background: theme.sidebarBg, border: `1px solid ${theme.sidebarBorder}` }}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider px-2 pb-1" style={{ color: theme.sidebarTextMuted }}>
                      Department Library
                    </p>
                    {libraryNavItems.map(({ to, icon: Icon, label, badge }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setLibraryOpen(false)}
                        className={flyoutLinkClass}
                        style={({ isActive }) => ({ color: isActive ? theme.sidebarActiveText : theme.sidebarText, background: isActive ? theme.sidebarActiveBg : 'transparent' })}
                        onMouseEnter={(e) => handleFlyoutHover(e, true)}
                        onMouseLeave={(e) => handleFlyoutHover(e, false)}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1">{label}</span>
                        {badge && (
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            {badge}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>

            {/* Legacy Migration flyout */}
            <div className="relative">
              <button
                ref={migrationBtnRef}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  background: (isOnMigration || migrationOpen) ? theme.sidebarActiveBg : 'transparent',
                  color: (isOnMigration || migrationOpen) ? theme.sidebarActiveText : theme.sidebarText,
                }}
                onClick={() => {
                  if (migrationOpen) {
                    setMigrationOpen(false);
                    return;
                  }
                  setAdminOpen(false);
                  setLibraryOpen(false);
                  setMigrationOpen(true);
                }}
              >
                <DatabaseZap className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">Legacy Migration</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: theme.sidebarTextMuted }} />
              </button>

              {migrationOpen && createPortal(
                <div
                  data-migration-flyout
                  className="fixed z-50"
                  style={(() => {
                    const btn = migrationBtnRef.current;
                    if (!btn) return { left: 264, top: 180 };
                    const rect = btn.getBoundingClientRect();
                    const panelH = 160;
                    const gap = 8;
                    const left = 256 + gap;
                    const maxTop = Math.max(8, window.innerHeight - panelH - 8);
                    const top = Math.min(Math.max(8, rect.top), maxTop);
                    return { left, top };
                  })()}
                >
                  <div
                    className="flex flex-col gap-1 p-2 rounded-xl shadow-2xl"
                    style={{ minWidth: '200px', background: theme.sidebarBg, border: `1px solid ${theme.sidebarBorder}` }}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider px-2 pb-1" style={{ color: theme.sidebarTextMuted }}>
                      Legacy Migration
                    </p>
                    {migrationNavItems.map(({ to, icon: Icon, label }) => (
                      <NavLink
                        key={to}
                        to={to}
                        onClick={() => setMigrationOpen(false)}
                        className={flyoutLinkClass}
                        style={({ isActive }) => ({ color: isActive ? theme.sidebarActiveText : theme.sidebarText, background: isActive ? theme.sidebarActiveBg : 'transparent' })}
                        onMouseEnter={(e) => handleFlyoutHover(e, true)}
                        onMouseLeave={(e) => handleFlyoutHover(e, false)}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>

            <div className="pt-1" style={{ borderTop: `1px solid ${theme.sidebarBorder}` }} />

            {/* Settings — direct link */}
            <NavLink
              to="/settings"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={({ isActive }) => navLinkStyle(isActive)}
              onMouseEnter={(e) => handleNavHover(e, true)}
              onMouseLeave={(e) => handleNavHover(e, false)}
            >
              <Settings className="h-4 w-4 shrink-0" />
              Settings
            </NavLink>

          </div>

          {/* Separator + Testing Tools */}
          <div className="pt-2 shrink-0" style={{ borderTop: `1px solid ${theme.sidebarBorder}` }}>
            <button
              onClick={() => setTestingOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
              style={{ color: theme.sidebarTextMuted }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = theme.sidebarHoverBg; (e.currentTarget as HTMLButtonElement).style.color = theme.sidebarText; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = theme.sidebarTextMuted; }}
            >
              <Beaker className="h-4 w-4 shrink-0" />
              Testing
            </button>
          </div>
        </nav>

        <div className="p-3 shrink-0" style={{ borderTop: `1px solid ${theme.sidebarBorder}` }}>
          <p className="px-3 text-xs" style={{ color: theme.sidebarTextMuted }}>
            Defensible v{formatDisplayVersion(import.meta.env.VITE_BUILD_VERSION)}
          </p>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          className="h-14 flex items-center justify-between gap-4 px-6 shrink-0"
          style={{ background: theme.headerBg, borderBottom: `1px solid ${theme.headerBorder}` }}
        >
          <nav className="flex items-center gap-1 text-sm flex-1 min-w-0">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path} className="flex items-center gap-1 min-w-0">
                {index > 0 && <ChevronRight size={14} className="shrink-0" style={{ color: theme.headerTextMuted }} />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="font-semibold truncate" style={{ color: theme.headerText }}>{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="truncate"
                    style={{ color: theme.headerTextMuted }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme.headerText}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme.headerTextMuted}
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
          {user && (
            <div className="flex items-center gap-3">
              <button
                onClick={toggleChat}
                className="flex items-center gap-2 rounded-lg px-3 py-1.5 transition-all duration-200 relative group"
                style={{
                  background: isChatOpen ? 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)' : 'transparent',
                  color: isChatOpen ? 'white' : theme.headerText,
                }}
                onMouseEnter={(e) => {
                  if (!isChatOpen) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #fb923c 0%, #ef4444 100%)';
                    (e.currentTarget as HTMLButtonElement).style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isChatOpen) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = theme.headerText;
                  }
                }}
                title="AI Assistant"
              >
                <img src={squirrelIconSmall} alt="AI" className="w-5 h-5 object-contain group-hover:scale-110 transition-transform" width="20" height="20" />
                <span className="text-sm font-medium hidden sm:inline">AI Assistant</span>
              </button>

              {/* Profile dropdown trigger + panel */}
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className="flex items-center gap-2 rounded-lg px-2 py-1 transition-colors"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = theme.sidebarHoverBg; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  title={user.name}
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: theme.accent }}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium leading-tight" style={{ color: theme.headerText }}>{user.name}</p>
                    <p className="text-xs leading-tight" style={{ color: theme.headerTextMuted }}>{user.email}</p>
                  </div>
                </button>

                {profileOpen && (
                  <UserProfileDropdown
                    user={{ name: user.name, email: user.email }}
                    theme={theme}
                    themes={THEMES}
                    effectiveThemeId={themeId}
                    userThemeId={userThemeId}
                    themeSource={themeSource}
                    onSelectTheme={(id) => {
                      const previousThemeId = themeId;
                      setThemeId(id);
                      void setUserThemeId(id).catch((err) => {
                        setThemeId(previousThemeId);
                        console.warn('[AdminLayout] Failed to save user theme preference:', err);
                      });
                      setProfileOpen(false);
                    }}
                    onClearOverride={userThemeId ? () => {
                      const previousThemeId = themeId;
                      setThemeId('default');
                      void setUserThemeId(null).catch((err) => {
                        setThemeId(previousThemeId);
                        console.warn('[AdminLayout] Failed to clear user theme preference:', err);
                      });
                      setProfileOpen(false);
                    } : undefined}
                    onSignOut={() => { setProfileOpen(false); logout(); }}
                  />
                )}
              </div>
            </div>
          )}
          <DefensibleDataWidget />
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto" style={{ background: theme.pageBg }}>
          <Outlet />
        </main>
      </div>

      <SystemTestingModal isOpen={testingOpen} onClose={() => setTestingOpen(false)} />
      <AIChatbot />
    </div>
  );
}
